import React, { useMemo, useCallback } from 'react';
import { Box, Button, Divider, Stack, Typography, CircularProgress, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { FieldErrors, FieldArrayWithId } from 'react-hook-form';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableQuestionItem from './SortableQuestionItem';
import { useCampaignQuestionsEditor } from '../context';

import type { CampaignQuestion } from '../types';
import type { FormValues } from '../utils/validationSchema';
import { useHighlightedItem } from '@/hooks';

interface QuestionFieldListProps {
    fields: FieldArrayWithId<FormValues, 'questions', 'fieldKey'>[];
    questionsToRemove?: number[];
    onQuestionDelete: (index: number) => void;
    onQuestionReorder: (activeIndex: number, overIndex: number) => void;
    handleSave: () => void;
    handleAddQuestion: () => void;
    errors?: FieldErrors<{ questions: CampaignQuestion[] }>;
    isLoading?: boolean;
}

/**
 * Editable list of questions with drag-and-drop support and a save button.
 */
const QuestionFieldList: React.FC<QuestionFieldListProps> = (props) => {
    const {
        fields,
        questionsToRemove = [],
        onQuestionDelete,
        onQuestionReorder,
        handleSave,
        handleAddQuestion,
        errors,
        isLoading = false,
    } = props;

    const sortableItems = useMemo(() => fields.map((q) => q.id), [fields]);
    const { highlightedItemId, highlightItem } = useHighlightedItem(sortableItems);

    const { isModalOpen } = useCampaignQuestionsEditor();

    // Sensors for drag and drop - memoized to prevent recreating on every render
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // Handles drag end event
    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                const oldIndex = fields.findIndex((q) => q.id === active.id);
                const newIndex = fields.findIndex((q) => q.id === over.id);

                if (oldIndex !== -1 && newIndex !== -1) {
                    onQuestionReorder(oldIndex, newIndex);

                    // Highlight the dragged question
                    highlightItem(active.id as string);
                }
            }
        },
        [fields, onQuestionReorder, highlightItem]
    );

    // Wrapper for onQuestionReorder that highlights the moved question
    const handleQuestionReorderWithHighlight = useCallback(
        (activeIndex: number, overIndex: number) => {
            onQuestionReorder(activeIndex, overIndex);

            // Highlight the question at its new position
            const movedQuestion = fields[activeIndex];

            highlightItem(movedQuestion.id || `question-${activeIndex}`);
        },
        [fields, onQuestionReorder, highlightItem]
    );

    // Disable sorting when a modal is open or when there are less than 2 questions
    const isSortingDisabled = isModalOpen || fields.length < 2;

    return (
        <Stack
            spacing={1}
            component="form"
            onSubmit={(e) => {
                e.preventDefault();
                handleSave();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'TEXTAREA') {
                    e.preventDefault();
                }
            }}
            sx={{ mb: 1 }}
        >
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                    {fields.map((field, index) => (
                        <SortableQuestionItem
                            key={field.fieldKey}
                            question={field as CampaignQuestion}
                            index={index}
                            questionsToRemove={questionsToRemove}
                            onQuestionDelete={onQuestionDelete}
                            onQuestionReorder={handleQuestionReorderWithHighlight}
                            error={errors?.questions?.[index]}
                            highlightedQuestionId={highlightedItemId}
                            isFirst={index === 0}
                            isLast={index === fields.length - 1}
                            isSortingDisabled={isSortingDisabled}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {fields.length > 0 && (
                <Box my={1}>
                    <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAddQuestion}>
                        Add another question
                    </Button>
                </Box>
            )}
            <Box position="sticky" bottom={0} bgcolor="background.paper" sx={{ zIndex: 100, pb: 1 }}>
                <Divider sx={{ mb: 1 }} />
                {errors?.questions && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        Some questions have errors. Please fix them before saving.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {fields.length} {fields.length === 1 ? 'question' : 'questions'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            type="submit"
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Stack>
    );
};

export default QuestionFieldList;
